import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableRow, IconButton, Paper, Checkbox, TableHead, Button, TablePagination, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, InputLabel, FormControl, Select, MenuItem, Tabs, Tab, Autocomplete } from '@mui/material';
import GestionTiposAnalisis from './GestionTiposAnalisis'; // Este componente debe manejar la adición y edición
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function TiposAnalisis() {
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;
    const [tiposAnalisis, setTiposAnalisis] = useState([]);
    const [editingAnalisis, setEditingAnalisis] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [analisisToDelete, setAnalisisToDelete] = useState(null);
    const { user } = useUser();

    // Estados para el modal de edición de código (orden)
    const [openOrderModal, setOpenOrderModal] = useState(false);
    const [selectedOrderAnalisis, setSelectedOrderAnalisis] = useState(null);
    const [newCodigoAnalisis, setNewCodigoAnalisis] = useState('');

    // Función para abrir el modal de edición del código
    const handleOpenOrderModal = (analisis) => {
        setSelectedOrderAnalisis(analisis);
        setNewCodigoAnalisis(analisis.CodigoAnalisis);
        setOpenOrderModal(true);
    };

    // Función para cerrar el modal de edición de código
    const handleCloseOrderModal = () => {
        setOpenOrderModal(false);
        setSelectedOrderAnalisis(null);
        setNewCodigoAnalisis('');
    };

    // Función auxiliar para actualizar el código de análisis en el backend
    const updateTipoAnalisisCode = async (analisisToUpdate) => {
        // Se construye el payload con el nuevo Código y el usuario que modificó
        const payload = {
            CodigoAnalisis: analisisToUpdate.CodigoAnalisis,
            modificadoPor: user ? user.codigoUsuario : 'ADMIN'
        };

        try {
            const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/${analisisToUpdate.idTipoAnalisis}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                console.error(`Error actualizando el análisis con id ${analisisToUpdate.idTipoAnalisis}: ${response.statusText}`);
            } else {
                console.log(`Análisis con id ${analisisToUpdate.idTipoAnalisis} actualizado correctamente`);
            }
        } catch (error) {
            console.error('Error al actualizar el análisis:', error);
        }
    };

    // Función para construir el payload completo a partir de un análisis (incluyendo la modificación del código)
    const buildFullPayload = (analysis) => {
        return {
            // Asegúrate de incluir aquí todos los campos que tu API requiere para una actualización.
            // Estos campos son los que se envían en el API POST, con la diferencia de que para actualizar se envía "modificadoPor" en lugar de "ingresadoPor".
            idTipoAnalisis: analysis.idTipoAnalisis,
            IdAcceso: analysis.IdAcceso, // Si aplica
            IdModulo: analysis.IdModulo,
            descripcionTipoAnalisis: analysis.descripcionTipoAnalisis,
            idClaseAnalisis: analysis.idClaseAnalisis,
            codigoDeBarras: analysis.codigoDeBarras,
            abreviatura: analysis.abreviatura,
            aliasAnalisis: analysis.aliasAnalisis,
            muestra: analysis.muestra,
            orden: analysis.orden,
            decimales: analysis.decimales,
            integra400Plus: analysis.integra400Plus,
            cm250: analysis.cm250,
            nombreComercial: analysis.nombreComercial,
            grafica: analysis.grafica,
            remitido: analysis.remitido,
            // Si tienes otros campos para el análisis, inclúyelos aquí.
            CodigoAnalisis: analysis.CodigoAnalisis, // Campo que se quiere actualizar
            modificadoPor: user ? user.codigoUsuario : 'ADMIN'
        };
    };

    // Función auxiliar para actualizar un análisis en el backend con el payload completo
    const updateTipoAnalisisFull = async (analysisToUpdate) => {
        const payload = buildFullPayload(analysisToUpdate);
        console.log('Payload a enviar para actualizar:', payload);
        try {
            const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/${analysisToUpdate.idTipoAnalisis}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) {
                console.error(`Error actualizando el análisis con id ${analysisToUpdate.idTipoAnalisis}: ${response.statusText}`);
            } else {
                console.log(`Análisis con id ${analysisToUpdate.idTipoAnalisis} actualizado correctamente`);
            }
        } catch (error) {
            console.error('Error al actualizar el análisis:', error);
        }
    };


    // Función para guardar el cambio de código y actualizar en backend
    // En la función para guardar el nuevo código (handleSaveOrderChange) se hacen los intercambios e
    // se utiliza la función updateTipoAnalisisFull para actualizar cada uno de los análisis que se modifiquen.
    // Función para guardar el cambio de código
    const handleSaveOrderChange = async () => {
        // Convertir el input a número; si el input está vacío, se considerará que no se ingresó nuevo código
        let newCode = newCodigoAnalisis !== '' ? parseInt(newCodigoAnalisis, 10) : NaN;

        // Filtrar solo los análisis que pertenecen al mismo grupo (mismo idModulo) que el seleccionado
        const grupo = tiposAnalisis.filter(item => item.idModulo === selectedOrderAnalisis.idModulo);

        if (isNaN(newCode)) {
            // Si no se ingresó código o no es un número, asignamos el siguiente código automáticamente:
            // Buscamos el máximo código existente en el grupo (ignorando aquellos que no sean numéricos)
            const codigosExistentes = grupo
                .map(item => parseInt(item.CodigoAnalisis, 10))
                .filter(num => !isNaN(num));
            const maxCodigo = codigosExistentes.length > 0 ? Math.max(...codigosExistentes) : 0;
            newCode = maxCodigo + 1;
        }

        // Ahora, dentro del grupo, buscamos si hay otro análisis (excluyendo el seleccionado) con el mismo nuevo código
        const conflictIndex = grupo.findIndex(item =>
            item.id !== selectedOrderAnalisis.id && parseInt(item.CodigoAnalisis, 10) === newCode
        );

        let updatedAnalisis = [...tiposAnalisis];

        if (conflictIndex !== -1) {
            // Caso: Existe otro análisis con ese código dentro del grupo: se hace intercambio de códigos
            const analysisToSwap = grupo[conflictIndex];
            const updatedSelected = { ...selectedOrderAnalisis, CodigoAnalisis: newCode };
            const updatedOther = { ...analysisToSwap, CodigoAnalisis: selectedOrderAnalisis.CodigoAnalisis };

            // Actualizar en el array general
            updatedAnalisis = updatedAnalisis.map(item => {
                if (item.id === updatedSelected.id) return updatedSelected;
                if (item.id === updatedOther.id) return updatedOther;
                return item;
            });

            // Actualizar ambos registros en el backend
            await updateTipoAnalisisFull(updatedSelected);
            await updateTipoAnalisisFull(updatedOther);
        } else {
            // Caso: No hay conflicto, actualizar el registro seleccionado
            updatedAnalisis = updatedAnalisis.map(item =>
                item.id === selectedOrderAnalisis.id ? { ...item, CodigoAnalisis: newCode } : item
            );
            const updatedItem = updatedAnalisis.find(item => item.id === selectedOrderAnalisis.id);
            await updateTipoAnalisisFull(updatedItem);
        }

        // Reordenar el array de análisis: primero por idModulo y luego por CodigoAnalisis
        updatedAnalisis.sort((a, b) => {
            if (a.idModulo === b.idModulo) {
                return parseInt(a.CodigoAnalisis, 10) - parseInt(b.CodigoAnalisis, 10);
            }
            return typeof a.idModulo === 'string'
                ? a.idModulo.localeCompare(b.idModulo)
                : a.idModulo - b.idModulo;
        });

        setTiposAnalisis(updatedAnalisis);
        handleCloseOrderModal();
    };
    // Función para cargar los tipos de análisis
    const fetchTiposAnalisis = async () => {
        try {
            const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis`);
            const data = await response.json();
            let analisis = data.$values || [];
            // Ordena por idModulo y luego por CodigoAnalisis
            analisis.sort((a, b) => {
                if (a.idModulo === b.idModulo) {
                    return a.CodigoAnalisis - b.CodigoAnalisis;
                }
                return typeof a.idModulo === 'string'
                    ? a.idModulo.localeCompare(b.idModulo)
                    : a.idModulo - b.idModulo;
            });
            setTiposAnalisis(analisis);
        } catch (error) {
            console.error('Error al cargar los tipos de análisis:', error);
        }
    };

    useEffect(() => {
        fetchTiposAnalisis();
    }, []);

    // Manejo para nuevos o edición desde el componente de formulario
    const handleNewAnalisis = () => {
        fetchTiposAnalisis();
        setEditingAnalisis(null);
    };

    const handleEditAnalisis = (analisis) => {
        console.log(analisis);
        setEditingAnalisis(analisis);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = (analisis) => {
        setAnalisisToDelete(analisis);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setAnalisisToDelete(null);
    };

    const handleDeleteAnalisis = async () => {
        if (analisisToDelete) {
            try {
                const username = '' + user.codigoUsuario + '/';
                const response = await fetch(`${server}/MgAnalisisGeneralTiposDeAnalisis/${analisisToDelete.idTipoAnalisis}/${username}`, {
                    method: 'DELETE'
                });
                if (response.ok) {
                    handleCloseDialog();
                    fetchTiposAnalisis();
                    Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: 'Tipo de análisis eliminado con éxito',
                    });
                } else {
                    handleCloseDialog();
                    const errorData = await response.json();
                    const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                    Swal.fire({
                        icon: 'error',
                        title: `Error ${response.status}`,
                        text: errorMessage,
                    });
                }
            } catch (error) {
                console.error('Error al eliminar el tipo de análisis:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error al eliminar el tipo de análisis: ' + error,
                });
            }
        }
    };

    const filteredAnalisis = tiposAnalisis.filter(analisis =>
        analisis.descripcionTipoAnalisis?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <div className="right_col" role="main">
                <div className="x_title"><h2>Tipos Análisis</h2></div>
                <div className="x_content">
                    <GestionTiposAnalisis
                        handleNewAnalisis={handleNewAnalisis}
                        tipoAnalisis={editingAnalisis}
                        handleClearTipoAnalisis={() => setEditingAnalisis(null)}
                    />

                    <h2>Tipos de Análisis Existentes</h2>
                    {/* <div className="row"> */}
                    {/* <div className="col-md-7 col-sm-5 offset-md-2"> */}
                    <TextField
                        variant="outlined"
                        label="Buscar tipo de análisis"
                        fullWidth
                        value={searchQuery}
                        onChange={handleSearchChange}
                        style={{ marginBottom: '20px' }}
                    />
                    <div className="table-responsive">
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="right">Acciones</TableCell>
                                        <TableCell>Modulo</TableCell>
                                        <TableCell>Descripción</TableCell>
                                        <TableCell>Nombre Comercial</TableCell>
                                        <TableCell>Ayuno</TableCell>
                                        <TableCell>Abreviatura</TableCell>
                                        <TableCell>Alias</TableCell>
                                        <TableCell>Código de Análisis</TableCell>
                                        <TableCell>Remitido</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAnalisis
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((analisis) => (
                                            <TableRow key={analisis.id}>
                                                <TableCell align="right">
                                                    <Button onClick={() => handleEditAnalisis(analisis)} color="primary" disabled={!tienePermiso('Editar Tipo Análisis')}>
                                                        Editar
                                                    </Button>
                                                    <Button onClick={() => handleOpenDialog(analisis)} color="secondary" disabled={!tienePermiso('Eliminar Tipo Análisis')}>
                                                        Eliminar
                                                    </Button>
                                                    {/* <Box display="flex" alignItems="center">
                              {analisis.CodigoAnalisis}
                              <IconButton color="primary" onClick={() => handleOpenOrderModal(analisis)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box> */}
                                                </TableCell>
                                                <TableCell>{analisis.idModulo}</TableCell>
                                                <TableCell>{analisis.descripcionTipoAnalisis}</TableCell>
                                                <TableCell>{analisis.nombreComercial}</TableCell>
                                                <TableCell>
                                                    <Checkbox checked={analisis.ayuno} disabled />
                                                </TableCell>
                                                <TableCell>{analisis.abreviatura}</TableCell>
                                                <TableCell>{analisis.aliasAnalisis}</TableCell>
                                                <TableCell>{analisis.CodigoAnalisis}</TableCell>
                                                <TableCell>
                                                    <Checkbox checked={analisis.remitido} disabled />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredAnalisis.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Filas por página:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                                }
                            />
                        </TableContainer>
                        <Dialog
                            open={openDialog}
                            onClose={handleCloseDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Confirmar eliminación"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    ¿Estás seguro de que deseas eliminar el tipo de análisis seleccionado?
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog} color="primary">
                                    Cancelar
                                </Button>
                                <Button onClick={handleDeleteAnalisis} color="primary" autoFocus>
                                    Eliminar
                                </Button>
                            </DialogActions>
                        </Dialog>
                        {openOrderModal && (
                            <Dialog open={openOrderModal} onClose={handleCloseOrderModal}>
                                <DialogTitle>Modificar Código de Análisis</DialogTitle>
                                <DialogContent>
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        label="Código de Análisis"
                                        type="number"
                                        fullWidth
                                        value={newCodigoAnalisis}
                                        onChange={(e) => setNewCodigoAnalisis(e.target.value)}
                                    />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseOrderModal} color="secondary">
                                        Cancelar
                                    </Button>
                                    <Button onClick={handleSaveOrderChange} variant="contained" color="primary" disabled={!tienePermiso('Editar Tipo Análisis')}>
                                        Guardar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        )}
                    </div>
                    {/* </div> */}
                </div>
                {/* </div> */}
            </div>
        </>
    );
}

export default TiposAnalisis;
